/**
 * Newsletter Already Subscribed Component
 * Friendly message when user is already subscribed
 */

import { useEffect, useState } from 'react';
import { CheckCircle2, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { dataLayerPushClickButton } from '@/lib/ga4';
import { useNewsletter } from '../hooks/useNewsletter';
import { MODAL_CONTENT } from '../constants';

export function NewsletterAlreadySubscribed() {
  const { closeModal, formData } = useNewsletter();
  const [showContent, setShowContent] = useState(false);

  const content = MODAL_CONTENT.alreadySubscribed;

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-8 tablet:px-6 tablet:py-10 text-center">
        {/* Icon */}
        <div
          className={cn(
            'inline-flex items-center justify-center w-20 h-20 rounded-full',
            'bg-gradient-to-br from-emerald-400 to-emerald-600',
            'shadow-lg shadow-emerald-500/30 mb-6',
            'transition-all duration-500',
            showContent ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
          )}
        >
          <CheckCircle2 className="w-10 h-10 text-white" />
        </div>

        {/* Text content */}
        <div
          className={cn(
            'transition-all duration-500 delay-150',
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          )}
        >
          <h3 className="text-xl tablet:text-2xl font-bold text-gray-900 mb-2">
            {content.title}
          </h3>

          {/* Email indicator */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 text-sm text-gray-700 mb-4">
            <Mail className="h-4 w-4 text-[#014383]" />
            <span className="font-medium">{formData.email}</span>
          </div>

          <p className="text-sm text-gray-600 max-w-sm mx-auto">
            {content.message.replace('{email}', formData.email)}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 px-5 py-4 tablet:px-6 border-t border-gray-100 bg-gray-50/50">
        <button
          type="button"
          onClick={() => {
            dataLayerPushClickButton(content.closeButton, 'Close');
            closeModal();
          }}
          className={cn(
            'w-full px-4 py-3 rounded-xl font-semibold text-sm',
            'bg-gray-100 text-gray-700 hover:bg-gray-200',
            'active:scale-[0.98] transition-all duration-200'
          )}
        >
          {content.closeButton}
        </button>
      </div>
    </div>
  );
}
